import {Component, EventEmitter, Input, Output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {PermissionService} from '../../../_auth/permission.service';
import {AppRoutes} from '../../../enums/routes';
import {CompanyRoutes} from '../../company/x-models/x-enums/company-routes';
import {LocationRoutes} from '../_models/_enums/location-routes';


/**
 * The details page of a **virtual event** (#664).
 *
 * A virtual event exists for planning only: no register ever picks it up, so it has no orders, no summary, no
 * employee sales, no price profiles and no export. Rendering the ordinary event-details page for one would
 * therefore show four aggregate tiles reading `0,00 €`, an empty transaction table and four toolbar buttons
 * that either 404 or answer `E_EVENT_VIRTUAL_HAS_NO_SALES` — a screen that looks broken while working exactly
 * as designed.
 *
 * So the page is replaced rather than emptied, and what it offers is the two things that *do* apply: the
 * shift plan and the event's own settings. Both are gated on the same permission sets the corresponding
 * buttons on the real page carry, so this screen never offers a door it cannot open.
 *
 * `settingsRequested` rather than its own `router.navigate`: the parent already owns that navigation
 * (`EventDetailsComponent.settings_redirect()`, which carries the page-size query param the back link needs),
 * and two copies of one route is one copy too many.
 */
@Component({
  selector: 'app-virtual-event-details',
  standalone: true,
  imports: [DatePipe, RouterLink],
  templateUrl: './virtual-event-details.component.html',
  styleUrl: './virtual-event-details.component.scss'
})
export class VirtualEventDetailsComponent {
  /** The event being shown. Always a virtual one — the parent decides which component to render. */
  @Input({required: true}) event!: VirtualEventHeader;

  /** The events list's page size, carried through the back link so returning lands on the same page. */
  @Input() last: number = 10;

  /** Raised by the "Einstellungen" tile; the parent performs the navigation. */
  @Output() settingsRequested = new EventEmitter<void>();

  protected readonly Routes = AppRoutes;
  protected readonly CompanyRoutes = CompanyRoutes;
  protected readonly LocationRoutes = LocationRoutes;

  constructor(public PermissionService: PermissionService) {
  }

  /** Same set as the real page's shift-plan button and as the shift maker's own route. */
  protected get canOpenShiftMaker(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.Permissions_ShiftMakerComponent());
  }

  /** Same set as the real page's settings (pencil) button. */
  protected get canOpenSettings(): boolean {
    return this.PermissionService.CheckPermission(this.PermissionService.Inventory_Event_Get());
  }

  /**
   * The shift plan opens on the event's own day. `toISOString()` matches what the real page's button sends,
   * so both entry points land the board on the same date.
   */
  protected get shiftMakerQueryParams(): {event: string, date: string} {
    return {event: this.event.id, date: new Date(this.event.start).toISOString()};
  }

  /** The events-list back link keeps the row highlighted and the page size, exactly as the real page does. */
  protected get backQueryParams(): {previous: string, last: number} {
    return {previous: this.event.id, last: this.last};
  }

  protected openSettings(): void {
    this.settingsRequested.emit();
  }
}

/**
 * The six fields this page renders off an event.
 *
 * A structural type rather than a payload class (Feature #760): the parent now holds an
 * `EventDetailsDto`, and naming what this screen reads is both what lets it take one and the honest
 * statement of its contract. `publicResponseEnd` is not optional beside `public` — the header prints
 * „Anmeldung bis {{ … }} Uhr" inside the `public` branch, and a missing date renders the literal sentence
 * with a hole in it rather than hiding the line (#664 / CAPTURE-ISSUES #63).
 */
export interface VirtualEventHeader {
  id: string;
  name: string;
  start: Date;
  end: Date;
  public: boolean;
  publicResponseEnd: Date;
}
